import React from 'react';
import type { ActionMeta, GroupBase, MultiValue, OnChangeValue, Props, PropsValue } from 'react-select';

import type { FieldRenderProps } from '@/js/libs/final-form/index.ts';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import type { InputIconsRendererProps } from '../common/index.ts';
import { InputIconsRenderer } from '../common/index.ts';
import { isErrorVisible } from '../Error/index.tsx';
import type { FormFieldPropsBase } from '../FormField/index.ts';
import { FinalFormFormField } from '../FormField/index.ts';
import {
  Select,
  StyledAsyncCreatableSelect,
  StyledCreatableSelect,
  AsyncSelect,
  MultiSelect,
  AsyncMultiSelect,
  CreatableMultiSelect,
  AsyncCreatableMultiSelect,
} from './index.tsx';
import type { Option } from './index.tsx';

// Final form field wrappers for react-select
type SelectComponentType =
  | typeof Select
  | typeof StyledAsyncCreatableSelect
  | typeof StyledCreatableSelect
  | typeof AsyncSelect
  | typeof MultiSelect
  | typeof AsyncMultiSelect
  | typeof CreatableMultiSelect
  | typeof AsyncCreatableMultiSelect;

type GetOptionType<TValue> = TValue extends MultiValue<infer ArrayOption> ? ArrayOption : NonNullable<TValue>;

/* eslint-disable @typescript-eslint/no-explicit-any -- Props<any> provides all react-select callback prop types with bivalent any params, avoiding contravariance conflicts and implicit-any */
type SelectFieldComponent = <TInputValue extends PropsValue<Option>, TFieldValue = TInputValue>(
  props: FieldRenderProps<TFieldValue, HTMLElement, TInputValue> &
    InputIconsRendererProps &
    FormFieldPropsBase &
    Partial<Props<any, any, GroupBase<any>>> & {
      [key: string]: unknown;
    },
) => React.ReactElement;
/* eslint-enable @typescript-eslint/no-explicit-any */

const createSelectField = (Component: SelectComponentType): SelectFieldComponent => {
  type FormComponentProps<TInputValue extends PropsValue<Option>, TFieldValue = TInputValue> = FieldRenderProps<
    TFieldValue,
    HTMLElement,
    TInputValue
  > &
    React.ComponentProps<typeof Component<GetOptionType<TInputValue>>> &
    InputIconsRendererProps &
    FormFieldPropsBase;

  const FormComponent = <TInputValue extends PropsValue<Option>, TFieldValue = TInputValue>(
    props: FormComponentProps<TInputValue, TFieldValue>,
  ) => {
    const {
      input,
      meta,
      label,
      labelTooltip,
      required,
      isDisabled,
      disabled: disabledProp,
      renderError,
      iconStart,
      iconEnd,
      withTouched,
      onChange,
      ...restProps
    } = props;
    const handleChange = useCallbackRef(
      (value: OnChangeValue<GetOptionType<TInputValue>, boolean>, actionMeta: ActionMeta<GetOptionType<TInputValue>>) => {
        input.onChange(value);
        // @ts-expect-error: isMulti false vs true
        onChange?.(value, actionMeta);
      },
    );
    const disabled = isDisabled || disabledProp;
    const { error, touched, submitError } = meta;

    return (
      <FinalFormFormField<string, TFieldValue>
        meta={meta}
        name={input.name}
        disabled={disabled}
        required={required}
        label={label}
        labelTooltip={labelTooltip}
        renderError={renderError}
        withTouched={withTouched}>
        <InputIconsRenderer iconStart={iconStart} iconEnd={iconEnd}>
          {/* @ts-expect-error: isMulti false vs true */}
          <Component<GetOptionType<TInputValue>>
            isDisabled={disabled}
            error={isErrorVisible(error, touched, withTouched, submitError)}
            {...input}
            // PropsValue doesn't consider the isMulti value
            value={input.value as PropsValue<GetOptionType<TInputValue>> | undefined}
            onChange={handleChange}
            {...restProps}
          />
        </InputIconsRenderer>
      </FinalFormFormField>
    );
  };

  return React.memo(FormComponent) as unknown as SelectFieldComponent;
};

export const SelectField = createSelectField(Select);
export const AsyncCreatableSelectField = createSelectField(StyledAsyncCreatableSelect);
export const CreatableSelectField = createSelectField(StyledCreatableSelect);
export const CreatableMultiSelectField = createSelectField(CreatableMultiSelect);
export const AsyncCreatableMultiSelectField = createSelectField(AsyncCreatableMultiSelect);
export const AsyncSelectField = createSelectField(AsyncSelect);
export const MultiSelectField = createSelectField(MultiSelect);
export const AsyncMultiSelectField = createSelectField(AsyncMultiSelect);
